/**
 *
 * @param array
 * @param key
 */
import { isNaN } from 'lodash'
import { FsColor } from './model'
import { Order } from './model/models'

export function groupBy<T, K extends keyof T>(array: T[], key: K) {
  const map = new Map<T[K], T[]>()
  array.forEach((item) => {
    // eslint-disable-next-line security/detect-object-injection
    const itemKey = item[key]
    if (!map.has(itemKey)) {
      map.set(
        itemKey,
        // eslint-disable-next-line security/detect-object-injection
        array.filter((i) => i[key] === item[key]),
      )
    }
  })
  return map
}

/**
 *
 * @param value
 * @param key
 */
export function orElseGet<T, K>(value: Map<K, T[]>, key: K): T[] {
  return value!.get(key) || []
}

export function colorForIndex(fsColor: FsColor, index: number): string | undefined {
  return fsColor.colors[parseInt(`${index}`, 10)]
}

export function translatedDomainName(name: string, t: any): string {
  let translatedName = name
  if (translatedName.match(/([0-9]*)(up_to_)([0-9]*)/)) {
    const repl = t('upto')
    translatedName = translatedName.replace('up_to_', repl)
  }
  if (translatedName.match(/([0-9]*)(to)([0-9]*)/)) {
    const repl = t('to')
    translatedName = translatedName.replace('to', repl)
  }
  if (translatedName.match(/([0-9]*)(_years)/)) {
    const yearsstr = translatedName.match(/([0-9]*)(_years)/)
    const years = yearsstr ? yearsstr[0].match(/\d+/g) : []
    const repl = years && Number(years) < 2 ? t('year') : t('years')
    translatedName = translatedName.replace('_years', ` ${repl}`)
  }
  if (translatedName.match(/([0-9]*)(_hours_per_year)/)) {
    const repl = t('hours_per_year')
    translatedName = translatedName.replace('_hours_per_year', ` ${repl}`)
  }
  if (translatedName.match(/([0-9]*)(_hours)/)) {
    const repl = t('hours')
    translatedName = translatedName.replace('_hours', ` ${repl}`)
  }
  if (translatedName.match(/(more_than_)([0-9]*)/)) {
    const repl = t('morethan')
    translatedName = translatedName.replace('more_than_', repl)
    translatedName = translatedName.replaceAll('_', '')
  }
  return t(`${translatedName}`)
}

export function drawExtraLine(context: any) {
  const { ctx } = context.chart
  const { width } = context.chart.canvas
  const { x, y, x2, y2, options } = context.element
  ctx.save()
  ctx.lineWidth = options.borderWidth
  ctx.strokeStyle = options.borderColor
  ctx.setLineDash([6, 6])
  ctx.lineDashOffset = options.borderDashOffset
  ctx.beginPath()
  ctx.moveTo(0, y)
  ctx.lineTo(x, y)
  ctx.moveTo(x2, y2)
  ctx.lineTo(width, y)
  ctx.stroke()
  ctx.restore()
  return true
}

export function formatDate(date: Date, lang: 'en' | 'de', config?: any) {
  return date.toLocaleDateString(
    lang === 'en' ? 'en-US' : 'de-DE',
    config ?? {
      day: 'numeric',
      month: 'long',
    },
  )
}

export function checkIfAvailableOnClone(clones: string[]) {
  const slug = process.env.REACT_APP_SLUG
  return slug ? clones.includes(slug) : false
}

export function validateEmail(email: string) {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    )
}

export function formatDateTime(date: Date, lang: 'en' | 'de', config?: any): string {
  return date.toLocaleString(
    lang === 'en' ? 'en-US' : 'de-DE',
    config ?? {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
  )
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const isNumeric = !isNaN(a[orderBy] ?? -10000) && !isNaN(b[orderBy] ?? -10000)
  if (isNumeric) {
    if (parseFloat(`${b[orderBy] ?? -10000}`) < parseFloat(`${a[orderBy] ?? -10000}`)) {
      return -1
    }
    if (parseFloat(`${b[orderBy] ?? -10000}`) > parseFloat(`${a[orderBy] ?? -10000}`)) {
      return 1
    }
    return 0
  }
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string | boolean | null },
  b: { [key in Key]: number | string | boolean | null },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

export const getFormattedString = (lang: string, num: string, min = 0, max = 1, isPlus = false) =>
  `${
    Number(Number(num).toFixed(max)) > 0.0
      ? isPlus
        ? '+'
        : ''
      : Number(Number(num).toFixed(max)) < 0.0
      ? '-'
      : ''
  } ${Number(Math.abs(Number(num))).toLocaleString(lang, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  })}`

export const roundUpToNearest10 = (value: number) => Math.ceil(value / 10) * 10
